// src/components/RotatingWords.tsx
//
// Palabras que rotan: la que se va sube y se desdibuja mientras la nueva entra
// desde abajo, en el mismo lugar. Cada palabra tiene su propio tiempo, para
// marcar un compás y quedarse más en las que importan.
//
// RESERVA EL LUGAR DE LA PALABRA MÁS LARGA (una copia invisible en la misma
// celda de grilla). Si el hueco cambiara de tamaño con cada palabra, todo lo
// que está debajo saltaría al ritmo de la rotación. En el celular "recetas
// electrónicas" ocupa dos renglones, y la reserva ya los incluye.
//
// Es decorativo: va con aria-hidden, y quien lo usa pone el texto completo
// para lectores de pantalla. Con prefers-reduced-motion queda fija la última
// palabra.

import React, { useState, useEffect, useRef } from 'react';

export interface RotatingWord {
  text: string;
  ms: number;
}

interface RotatingWordsProps {
  words: RotatingWord[];
  className?: string;
  // Avisa qué palabra se muestra (también la inicial), para que otra parte de
  // la pantalla acompañe la rotación: en el hero, el subtítulo resalta la misma.
  onChange?: (indice: number) => void;
}

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const RotatingWords: React.FC<RotatingWordsProps> = ({ words, className = '', onChange }) => {
  const reduced = prefersReduced();
  const [i, setI] = useState(reduced ? Math.max(0, words.length - 1) : 0);
  const [anterior, setAnterior] = useState<number | null>(null);

  // En una ref: si el padre pasa una función nueva en cada render, igual se
  // avisa solo cuando cambia la palabra.
  const avisar = useRef(onChange);
  avisar.current = onChange;
  useEffect(() => {
    avisar.current?.(i);
  }, [i]);

  useEffect(() => {
    if (reduced || words.length <= 1) return;
    const t = setTimeout(() => {
      setAnterior(i);
      setI((i + 1) % words.length);
    }, words[i]?.ms ?? 1200);
    return () => clearTimeout(t);
  }, [i, words, reduced]);

  const masLarga = words.reduce((a, w) => (w.text.length > a.length ? w.text : a), '');

  return (
    <span className="rotating-slot" aria-hidden="true">
      <span className={`rotating-sizer ${className}`}>{masLarga}</span>
      {anterior !== null && (
        <span key={`sale-${anterior}-${i}`} className={`rotating-word-out ${className}`}>
          {words[anterior]?.text}
        </span>
      )}
      {/* key nueva en cada cambio: remonta y vuelve a disparar la entrada */}
      <span key={`entra-${i}`} className={`rotating-word ${className}`}>
        {words[i]?.text}
      </span>
    </span>
  );
};

export default RotatingWords;
